import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { UserPlus, Repeat, Users } from 'lucide-react'
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts'
import { api } from '../lib/api'
import { formatCurrency } from '../lib/utils'
import PeriodFilter, { type Period } from '../components/PeriodFilter'

const TIER_LABELS: Record<string, string> = {
  bottled: 'Bottled · معبّأ',
  aged:    'Aged · معتّق',
  vintage: 'Vintage · نادر',
}

const TIER_COLORS: Record<string, string> = {
  bottled: '#9CA3AF',
  aged:    '#F59E0B',
  vintage: '#C41E3A',
}

interface CustomersOverview {
  new_customers: number
  active_customers: number
  returning_customers: number
  new_customer_orders: number
  repeat_rate: number
}

interface TierRow { tier: string; user_count: number }
interface TopCustomer { id: string; full_name: string; phone: string; loyalty_points: number; order_count: number; total_spent_fils: number }

export default function CustomerTrends() {
  const [period, setPeriod] = useState<Period>('30d')
  const params = `period=${period}`

  const overview  = useQuery<CustomersOverview>({ queryKey: ['cust-overview', period], queryFn: () => api.get(`/analytics/customers-overview?${params}`).then(r => r.data) })
  const tiers     = useQuery<{ tiers: TierRow[]; thresholds: { aged: number; vintage: number } }>({ queryKey: ['tier-dist'], queryFn: () => api.get(`/analytics/tier-distribution`).then(r => r.data) })
  const topCust   = useQuery<{ customers: TopCustomer[] }>({ queryKey: ['top-cust', period], queryFn: () => api.get(`/analytics/top-customers?${params}&limit=15`).then(r => r.data) })

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white">Customer Trends</h1>
        <PeriodFilter value={period} onChange={setPeriod} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
        <Kpi icon={<UserPlus size={18} />} label="New signups" value={`${overview.data?.new_customers ?? 0}`} />
        <Kpi icon={<Users size={18} />} label="Active customers" value={`${overview.data?.active_customers ?? 0}`} sublabel="placed ≥1 order" />
        <Kpi icon={<Repeat size={18} />} label="Returning" value={`${overview.data?.returning_customers ?? 0}`} sublabel={`${overview.data?.new_customer_orders ?? 0} first-time orderers`} />
        <Kpi icon={<Repeat size={18} />} label="Repeat rate" value={`${((overview.data?.repeat_rate ?? 0) * 100).toFixed(1)}%`} sublabel="of active customers" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Tier distribution */}
        <Card title="Loyalty tier distribution">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={tiers.data?.tiers ?? []}
                  dataKey="user_count"
                  nameKey="tier"
                  cx="50%" cy="50%"
                  outerRadius={80}
                  label={(entry: any) => entry.tier ? (TIER_LABELS[entry.tier as string] ?? entry.tier) : ''}
                  labelLine={false}
                >
                  {(tiers.data?.tiers ?? []).map((t, i) => (
                    <Cell key={i} fill={TIER_COLORS[t.tier] ?? '#9CA3AF'} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ background: '#1a1a1a', border: '1px solid #2e2e2e', borderRadius: 8 }}
                  formatter={(v) => [String(v), 'Customers']}
                />
                <Legend wrapperStyle={{ fontSize: 11, color: '#9CA3AF' }}
                  formatter={(v) => TIER_LABELS[v] ?? v} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          {tiers.data?.thresholds && (
            <p className="text-[10px] text-gray-500 mt-2 text-center">
              Aged ≥ {tiers.data.thresholds.aged} pts · Vintage ≥ {tiers.data.thresholds.vintage} pts
            </p>
          )}
        </Card>

        {/* Top customers */}
        <Card title={`Top customers · ${period}`}>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-500 text-[10px] uppercase tracking-wide">
                <th className="text-left pb-2 font-semibold">Customer</th>
                <th className="text-right pb-2 font-semibold">Orders</th>
                <th className="text-right pb-2 font-semibold">Spend</th>
              </tr>
            </thead>
            <tbody>
              {(topCust.data?.customers ?? []).map(c => (
                <tr key={c.id} className="border-t border-[#2e2e2e]">
                  <td className="py-2">
                    <p className="text-white truncate max-w-[200px]">{c.full_name}</p>
                    <p className="text-[10px] text-gray-500">{c.phone} · {c.loyalty_points} pts</p>
                  </td>
                  <td className="py-2 text-right text-gray-300 tabular-nums">{c.order_count}</td>
                  <td className="py-2 text-right text-white tabular-nums font-semibold">{formatCurrency(c.total_spent_fils)}</td>
                </tr>
              ))}
              {topCust.data?.customers?.length === 0 && (
                <tr><td colSpan={3} className="py-6 text-center text-gray-500 text-xs">No customers ordered in this period.</td></tr>
              )}
            </tbody>
          </table>
        </Card>
      </div>
    </div>
  )
}

function Kpi({ icon, label, value, sublabel }: { icon: React.ReactNode; label: string; value: string; sublabel?: string }) {
  return (
    <div className="bg-white/[0.02] border border-[#2e2e2e] rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-gray-400 text-xs">{label}</span>
        <span className="text-[#C41E3A]">{icon}</span>
      </div>
      <p className="text-xl font-bold text-white tabular-nums">{value}</p>
      {sublabel && <p className="text-[10px] text-gray-500 mt-1">{sublabel}</p>}
    </div>
  )
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white/[0.02] border border-[#2e2e2e] rounded-lg p-4">
      <h3 className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-3">{title}</h3>
      {children}
    </div>
  )
}
